import { useState, useMemo, useEffect } from 'react';
import { PublicLinkInfo, AvailabilityDay, AvailabilitySlot, bookingApi } from '../services/api';
import { CalendlyCustomFormFields } from './CustomFormFields';
import {
    ChevronLeft,
    ChevronRight,
    Clock,
    Globe,
    Check,
    User,
    Mail,
    MessageSquare,
    ArrowLeft
} from 'lucide-react';
import { TIMEZONES, getTimezoneLabel } from '../utils/timezones';

const DAYS_OF_WEEK = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

interface SelectedSlot extends AvailabilitySlot {
    date: string;
    day_name: string;
}

interface CalendlyStyleEmbedProps {
    link: PublicLinkInfo;
    availability: AvailabilityDay[];
    timezone: string;
    onTimezoneChange: (tz: string) => void;
    slug: string;
}

type Step = 'select' | 'form' | 'success';

export function CalendlyStyleEmbed({
    link,
    availability,
    timezone,
    onTimezoneChange,
    slug
}: CalendlyStyleEmbedProps) {
    // State
    const [step, setStep] = useState<Step>('select');
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<SelectedSlot | null>(null);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [customFields, setCustomFields] = useState<Record<string, any>>({});
    const [customFieldErrors, setCustomFieldErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Calendar view month - compute initial month from availability
    const initialMonth = useMemo(() => {
        if (availability.length > 0) {
            const firstAvailable = availability.find(d => d.slots.length > 0);
            if (firstAvailable) {
                const firstDate = new Date(firstAvailable.date + 'T00:00:00');
                return { year: firstDate.getFullYear(), month: firstDate.getMonth() };
            }
            const firstDate = new Date(availability[0].date + 'T00:00:00');
            return { year: firstDate.getFullYear(), month: firstDate.getMonth() };
        }
        const today = new Date();
        return { year: today.getFullYear(), month: today.getMonth() };
    }, [availability]);

    const [viewMonth, setViewMonth] = useState(initialMonth);

    // Reset state when timezone/availability changes
    useEffect(() => {
        // Reset selections when availability data changes (e.g., timezone change)
        setSelectedDate(null);
        setSelectedSlot(null);
        setStep('select');
        
        // Update viewMonth to show the first available date
        if (availability.length > 0) {
            const firstAvailable = availability.find(d => d.slots.length > 0);
            if (firstAvailable) {
                const firstDate = new Date(firstAvailable.date + 'T00:00:00');
                setViewMonth({ year: firstDate.getFullYear(), month: firstDate.getMonth() });
            } else {
                const firstDate = new Date(availability[0].date + 'T00:00:00');
                setViewMonth({ year: firstDate.getFullYear(), month: firstDate.getMonth() });
            }
        }
    }, [timezone, availability]);

    // Build set of available dates
    const availableDatesSet = useMemo(() => {
        return new Set(availability.filter(d => d.slots.length > 0).map(d => d.date));
    }, [availability]);

    // Get date range for navigation limits
    const dateRange = useMemo(() => {
        if (availability.length === 0) return { min: null, max: null };
        const dates = availability.map(d => d.date).sort();
        return { min: dates[0], max: dates[dates.length - 1] };
    }, [availability]);

    // Generate calendar grid
    const calendarDays = useMemo(() => {
        const { year, month } = viewMonth;
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        const startDayOfWeek = firstDayOfMonth.getDay();
        const daysInMonth = lastDayOfMonth.getDate();

        const days: { date: string; day: number; isCurrentMonth: boolean; isToday: boolean }[] = [];

        // Previous month's trailing days
        const prevMonth = new Date(year, month, 0);
        const prevMonthDays = prevMonth.getDate();
        for (let i = startDayOfWeek - 1; i >= 0; i--) {
            const day = prevMonthDays - i;
            const prevYear = month === 0 ? year - 1 : year;
            const prevMonthNum = month === 0 ? 11 : month - 1;
            const dateStr = `${prevYear}-${String(prevMonthNum + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            days.push({ date: dateStr, day, isCurrentMonth: false, isToday: false });
        }

        // Current month's days
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            days.push({ date: dateStr, day, isCurrentMonth: true, isToday: dateStr === todayStr });
        }

        // Next month's leading days (fill to 6 rows)
        const remainingDays = 42 - days.length;
        for (let day = 1; day <= remainingDays; day++) {
            const nextYear = month === 11 ? year + 1 : year;
            const nextMonthNum = month === 11 ? 0 : month + 1;
            const dateStr = `${nextYear}-${String(nextMonthNum + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            days.push({ date: dateStr, day, isCurrentMonth: false, isToday: false });
        }

        return days;
    }, [viewMonth]);

    // Get slots for selected date
    const slotsForSelectedDate = useMemo(() => {
        if (!selectedDate) return [];
        const day = availability.find(d => d.date === selectedDate);
        return day?.slots || [];
    }, [selectedDate, availability]);

    // Get day info for selected date
    const selectedDayInfo = useMemo(() => {
        if (!selectedDate) return null;
        return availability.find(d => d.date === selectedDate);
    }, [selectedDate, availability]);

    // Navigation helpers
    const canGoPrev = dateRange.min ? (() => {
        const minDate = new Date(dateRange.min + 'T00:00:00');
        return viewMonth.year > minDate.getFullYear() ||
            (viewMonth.year === minDate.getFullYear() && viewMonth.month > minDate.getMonth());
    })() : false;

    const canGoNext = dateRange.max ? (() => {
        const maxDate = new Date(dateRange.max + 'T00:00:00');
        return viewMonth.year < maxDate.getFullYear() ||
            (viewMonth.year === maxDate.getFullYear() && viewMonth.month < maxDate.getMonth());
    })() : false;

    const goToPrevMonth = () => {
        setViewMonth(prev => {
            if (prev.month === 0) {
                return { year: prev.year - 1, month: 11 };
            }
            return { year: prev.year, month: prev.month - 1 };
        });
    };

    const goToNextMonth = () => {
        setViewMonth(prev => {
            if (prev.month === 11) {
                return { year: prev.year + 1, month: 0 };
            }
            return { year: prev.year, month: prev.month + 1 };
        });
    };

    // Handle date selection
    const handleDateSelect = (date: string) => {
        const d = new Date(date + 'T00:00:00');
        setViewMonth({ year: d.getFullYear(), month: d.getMonth() });
        setSelectedDate(date);
        setSelectedSlot(null);
    };

    // Handle time selection
    const handleTimeSelect = (slot: AvailabilitySlot) => {
        if (selectedDayInfo) {
            setSelectedSlot({
                ...slot,
                date: selectedDate!,
                day_name: selectedDayInfo.day_name
            });
        }
    };

    // Confirm time and go to form
    const handleConfirmTime = () => {
        if (selectedSlot) {
            setStep('form');
        }
    };

    // Go back to time selection
    const handleBackToSelect = () => {
        setStep('select');
        setError(null);
    };

    // Submit booking
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedSlot || !name.trim() || !email.trim()) return;

        setIsSubmitting(true);
        setError(null);
        setCustomFieldErrors({});

        try {
            const isDirectBooking = link.booking_type === 'direct';

            // Prepare custom fields data (only include fields with values)
            const customFieldsData = Object.keys(customFields).length > 0 ? customFields : undefined;

            if (isDirectBooking) {
                await bookingApi.directBook(slug, {
                    requester_name: name.trim(),
                    requester_email: email.trim(),
                    message: message.trim() || undefined,
                    custom_fields: customFieldsData,
                    slot: {
                        start_time_utc: selectedSlot.start_time_utc,
                        end_time_utc: selectedSlot.end_time_utc
                    },
                    timezone
                });
            } else {
                await bookingApi.submit(slug, {
                    requester_name: name.trim(),
                    requester_email: email.trim(),
                    message: message.trim() || undefined,
                    custom_fields: customFieldsData,
                    slots: [{
                        start_time_utc: selectedSlot.start_time_utc,
                        end_time_utc: selectedSlot.end_time_utc
                    }],
                    timezone
                });
            }

            setStep('success');
        } catch (err: any) {
            // Check if error contains field validation errors
            if (err.errors && typeof err.errors === 'object') {
                setCustomFieldErrors(err.errors);
            }
            setError(err.message || 'Failed to submit booking');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Format date for display
    const formatDateDisplay = (dateStr: string) => {
        const date = new Date(dateStr + 'T12:00:00');
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Get current time in timezone for display
    const getCurrentTimeDisplay = () => {
        try {
            const now = new Date();
            const timeStr = now.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZone: timezone
            });
            return timeStr.toLowerCase();
        } catch {
            return '';
        }
    };

    const isDirectBooking = link.booking_type === 'direct';

    // Success screen
    if (step === 'success') {
        return (
            <div className="calendly-embed">
                <div className="calendly-success">
                    <div className="calendly-success-icon">
                        <Check size={48} />
                    </div>
                    <h2>{isDirectBooking ? 'You are scheduled' : 'Request submitted'}</h2>
                    <p>
                        {isDirectBooking
                            ? `A calendar invitation has been sent to ${email}`
                            : `Your request has been sent to ${link.user_name}. You'll receive an email when they confirm.`
                        }
                    </p>
                    <div className="calendly-success-details">
                        <div className="calendly-success-detail">
                            <strong>{link.name}</strong>
                        </div>
                        <div className="calendly-success-detail">
                            <Clock size={16} />
                            {selectedSlot && formatDateDisplay(selectedSlot.date)}
                        </div>
                        <div className="calendly-success-detail">
                            <Clock size={16} />
                            {selectedSlot?.start_time_local} - {selectedSlot?.end_time_local}
                        </div>
                        <div className="calendly-success-detail">
                            <Globe size={16} />
                            <span>{getTimezoneLabel(timezone)}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Form screen
    if (step === 'form') {
        return (
            <div className="calendly-embed">
                {/* Left Info Panel */}
                <div className="calendly-info-panel">
                    {link.logo_url && (
                        <img src={link.logo_url} alt="" className="calendly-logo" />
                    )}
                    <div className="calendly-user-name">{link.user_name}</div>
                    <h1 className="calendly-event-title">{link.name}</h1>
                    <div className="calendly-event-meta">
                        <Clock size={16} />
                        <span>{link.duration_minutes} min</span>
                    </div>
                    {link.description && (
                        <p className="calendly-event-description">{link.description}</p>
                    )}

                    <div className="calendly-selected-time">
                        <div className="calendly-selected-time-date">
                            {selectedSlot && formatDateDisplay(selectedSlot.date)}
                        </div>
                        <div className="calendly-selected-time-slot">
                            {selectedSlot?.start_time_local} - {selectedSlot?.end_time_local}
                        </div>
                        <div className="calendly-selected-time-tz">
                            {getTimezoneLabel(timezone)}
                        </div>
                    </div>
                </div>

                {/* Right Form Panel */}
                <div className="calendly-form-panel">
                    <button className="calendly-back-btn" onClick={handleBackToSelect}>
                        <ArrowLeft size={16} />
                        Back
                    </button>

                    {!link.embed_hide_title && (
                        <h2 className="calendly-form-title">Enter Details</h2>
                    )}

                    {error && (
                        <div className="calendly-error">{error}</div>
                    )}

                    <form onSubmit={handleSubmit} className="calendly-form">
                        <div className="calendly-form-group">
                            {!link.embed_hide_labels && <label className="calendly-label">Name *</label>}
                            <div className="calendly-input-wrapper">
                                <User size={18} />
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Your name"
                                    required
                                    className="calendly-input"
                                />
                            </div>
                        </div>

                        <div className="calendly-form-group">
                            {!link.embed_hide_labels && <label className="calendly-label">Email *</label>}
                            <div className="calendly-input-wrapper">
                                <Mail size={18} />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="your@email.com"
                                    required
                                    className="calendly-input"
                                />
                            </div>
                        </div>

                        {/* Custom form fields */}
                        {link.custom_fields && link.custom_fields.length > 0 && (
                            <CalendlyCustomFormFields
                                fields={link.custom_fields}
                                values={customFields}
                                onChange={(fieldName, value) => {
                                    setCustomFields(prev => ({ ...prev, [fieldName]: value }));
                                    // Clear error for this field when user types
                                    if (customFieldErrors[fieldName]) {
                                        setCustomFieldErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors[fieldName];
                                            return newErrors;
                                        });
                                    }
                                }}
                                errors={customFieldErrors}
                                hideLabels={link.embed_hide_labels}
                            />
                        )}

                        <div className="calendly-form-group">
                            {!link.embed_hide_labels && (
                                <label className="calendly-label">
                                    Please share anything that will help prepare for our meeting.
                                </label>
                            )}
                            <div className="calendly-input-wrapper textarea">
                                <MessageSquare size={18} />
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Optional message..."
                                    rows={3}
                                    className="calendly-textarea"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting || !name.trim() || !email.trim()}
                            className="calendly-submit-btn"
                        >
                            {isSubmitting ? 'Scheduling...' : 'Schedule Event'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // Main select screen
    return (
        <div className="calendly-embed">
            {/* Left Info Panel */}
            <div className="calendly-info-panel">
                {link.logo_url && (
                    <img src={link.logo_url} alt="" className="calendly-logo" />
                )}
                <div className="calendly-user-name">{link.user_name}</div>
                <h1 className="calendly-event-title">{link.name}</h1>
                <div className="calendly-event-meta">
                    <Clock size={16} />
                    <span>{link.duration_minutes} min</span>
                </div>
                {link.description && (
                    <p className="calendly-event-description">{link.description}</p>
                )}
            </div>

            {/* Right Booking Panel */}
            <div className="calendly-booking-panel">
                <div className={`calendly-calendar-section ${selectedDate ? 'has-times' : ''}`}>
                    <h2 className="calendly-section-title">Select a Date & Time</h2>

                    {/* Calendar Header */}
                    <div className="calendly-calendar-header">
                        <button
                            onClick={goToPrevMonth}
                            disabled={!canGoPrev}
                            className="calendly-nav-btn"
                            aria-label="Previous month"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <span className="calendly-month-title">
                            {MONTH_NAMES[viewMonth.month]} {viewMonth.year}
                        </span>
                        <button
                            onClick={goToNextMonth}
                            disabled={!canGoNext}
                            className="calendly-nav-btn"
                            aria-label="Next month"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>

                    {/* Weekday Headers */}
                    <div className="calendly-weekdays">
                        {DAYS_OF_WEEK.map((day, i) => (
                            <div key={i} className="calendly-weekday">{day}</div>
                        ))}
                    </div>

                    {/* Calendar Grid */}
                    <div className="calendly-calendar-grid">
                        {calendarDays.map((day, i) => {
                            const isAvailable = availableDatesSet.has(day.date);
                            const isSelected = day.date === selectedDate;
                            const isPast = day.date < new Date().toISOString().split('T')[0];
                            const isDisabled = !isAvailable || isPast;

                            return (
                                <button
                                    key={i}
                                    onClick={() => isAvailable && !isPast && handleDateSelect(day.date)}
                                    disabled={isDisabled}
                                    className={`calendly-day ${day.isCurrentMonth ? 'current-month' : 'other-month'} ${isAvailable && !isPast ? 'available' : ''} ${isSelected ? 'selected' : ''} ${day.isToday ? 'today' : ''}`}
                                >
                                    <span className="calendly-day-number">{day.day}</span>
                                    {day.isToday && <span className="calendly-today-dot" />}
                                </button>
                            );
                        })}
                    </div>

                    {/* Timezone Selector */}
                    <div className="calendly-timezone">
                        <span className="calendly-timezone-label">Time zone</span>
                        <div className="calendly-timezone-select">
                            <Globe size={14} />
                            <select
                                value={timezone}
                                onChange={(e) => onTimezoneChange(e.target.value)}
                            >
                                {TIMEZONES.map(tz => (
                                    <option key={tz.value} value={tz.value}>
                                        {tz.label} ({getCurrentTimeDisplay()})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Time Slots Panel - slides in */}
                <div className={`calendly-times-panel ${selectedDate ? 'visible' : ''}`}>
                    {selectedDate && (
                        <>
                            <h3 className="calendly-times-title">
                                {selectedDayInfo?.day_name}, {' '}
                                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                            </h3>
                            <div className="calendly-times-list">
                                {slotsForSelectedDate.map((slot, idx) => {
                                    const isSelected = selectedSlot?.start_time_utc === slot.start_time_utc;
                                    return (
                                        <div key={idx} className="calendly-time-row">
                                            <button
                                                onClick={() => handleTimeSelect(slot)}
                                                className={`calendly-time-btn ${isSelected ? 'selected' : ''}`}
                                            >
                                                {slot.start_time_local}
                                            </button>
                                            {isSelected && (
                                                <button
                                                    onClick={handleConfirmTime}
                                                    className="calendly-confirm-btn"
                                                >
                                                    Confirm
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CalendlyStyleEmbed;
